import React, { useState, useEffect } from 'react';
import { Gift, Plus, Copy, Check, Power, Search, Clock, Mail, ShieldAlert } from 'lucide-react';
import type { GiftCard } from '@zarin/types';
import { apiFetch } from '../lib/auth';

interface DisplayGiftCard {
  id: string;
  code: string;
  initialBalanceMinor: number;
  currentBalanceMinor: number;
  customerEmail?: string | undefined;
  recipientEmail?: string | undefined;
  recipientName?: string | undefined;
  message?: string | undefined;
  expiresAt?: string | undefined;
  isActive: boolean;
  createdAt: string;
}

const INITIAL_GIFTCARDS: DisplayGiftCard[] = [];

export default function GiftCardsPage() {
  const [giftCards, setGiftCards] = useState<DisplayGiftCard[]>(INITIAL_GIFTCARDS);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [search, setSearch] = useState('');

  useEffect(() => {
    async function loadGiftCards() {
      try {
        setLoading(true);
        const res = await apiFetch<{ data: any[] }>('/gift-cards');
        if (Array.isArray(res.data)) {
          setGiftCards(res.data.map((g: any) => ({
            id: g.id,
            code: g.code,
            initialBalanceMinor: g.initial_balance_minor || g.initialBalanceMinor || 0,
            currentBalanceMinor: g.current_balance_minor ?? g.currentBalanceMinor ?? 0,
            customerEmail: g.customer_email || g.customerEmail,
            recipientEmail: g.recipient_email || g.recipientEmail,
            recipientName: g.recipient_name || g.recipientName,
            message: g.message,
            expiresAt: g.expires_at || g.expiresAt,
            isActive: Boolean(g.is_active ?? g.isActive ?? true),
            createdAt: g.created_at || g.createdAt || new Date().toISOString(),
          })));
        }
      } catch (err) {
        console.error('Failed to load gift cards:', err);
      } finally {
        setLoading(false);
      }
    }
    loadGiftCards();
  }, []);

  // Form states
  const [amount, setAmount] = useState('5000');
  const [recipientEmail, setRecipientEmail] = useState('');
  const [recipientName, setRecipientName] = useState('');
  const [message, setMessage] = useState('');
  const [customCode, setCustomCode] = useState('');

  const generateRandomCode = () => {
    const chars = '23456789ABCDEFGHJKLMNPQRSTUVWXYZ';
    const seg = (len: number) => Array.from({ length: len }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
    return `ZARIN-${seg(4)}-${seg(4)}-${seg(4)}`;
  };

  const handleCreate = (e: React.FormEvent) => {
    e.preventDefault();
    const code = customCode.trim() || generateRandomCode();
    const newCard: DisplayGiftCard = {
      id: `gc_${Date.now()}`,
      code,
      initialBalanceMinor: Math.round(Number(amount) * 100),
      currentBalanceMinor: Math.round(Number(amount) * 100),
      recipientEmail: recipientEmail.trim() || undefined,
      recipientName: recipientName.trim() || undefined,
      message: message.trim() || undefined,
      expiresAt: new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString().split('T')[0],
      isActive: true,
      createdAt: new Date().toISOString(),
    };
    setGiftCards([newCard, ...giftCards]);
    setShowModal(false);
    setRecipientEmail('');
    setRecipientName('');
    setMessage('');
    setCustomCode('');
  };

  const toggleActive = (id: string) => {
    setGiftCards(prev => prev.map(c => c.id === id ? { ...c, isActive: !c.isActive } : c));
  };

  const copyCode = (id: string, code: string) => {
    navigator.clipboard.writeText(code);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const filteredCards = giftCards.filter(c =>
    c.code.toLowerCase().includes(search.toLowerCase()) ||
    (c.recipientEmail && c.recipientEmail.toLowerCase().includes(search.toLowerCase())) ||
    (c.recipientName && c.recipientName.toLowerCase().includes(search.toLowerCase()))
  );

  return (
    <div style={styles.page}>
      <div style={styles.header}>
        <div>
          <h1 style={styles.title}>Digital Gift Cards</h1>
          <p style={styles.subtitle}>Issue, track balances, and manage store credit certificates</p>
        </div>
        <button style={styles.btnPrimary} onClick={() => { setCustomCode(generateRandomCode()); setShowModal(true); }}>
          <Plus size={16} />
          <span>Issue Gift Card</span>
        </button>
      </div>

      <div style={styles.toolbar}>
        <div style={styles.searchBox}>
          <Search size={16} color="#888" />
          <input
            type="text"
            placeholder="Search by gift card code or recipient..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={styles.searchInput}
          />
        </div>
      </div>

      <div style={styles.tableCard}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Card Code</th>
              <th style={styles.th}>Recipient</th>
              <th style={styles.th}>Remaining Balance</th>
              <th style={styles.th}>Initial Value</th>
              <th style={styles.th}>Status</th>
              <th style={styles.th}>Expires</th>
              <th style={styles.thRight}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredCards.length === 0 ? (
              <tr>
                <td colSpan={7} style={{ padding: '48px 24px', textAlign: 'center', color: '#8A8A8E' }}>
                  {loading ? 'Loading gift cards…' : 'No gift card vouchers issued yet. Click "Issue New Gift Card" above to create store credit.'}
                </td>
              </tr>
            ) : (
              filteredCards.map((card) => {
                const percentRemaining = Math.round((card.currentBalanceMinor / (card.initialBalanceMinor || 1)) * 100);
                return (
                  <tr key={card.id} style={styles.tr}>
                    <td style={styles.td}>
                      <div style={styles.codeRow}>
                        <Gift size={15} color="#C9A84C" />
                        <code style={styles.codeText}>{card.code}</code>
                        <button
                          onClick={() => copyCode(card.id, card.code)}
                          style={styles.iconBtn}
                          title="Copy Gift Card Code"
                        >
                          {copiedId === card.id ? <Check size={13} color="#10B981" /> : <Copy size={13} color="#888" />}
                        </button>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div>
                        <div style={{ color: '#FFF', fontWeight: 500 }}>{card.recipientName || 'Unassigned'}</div>
                        <div style={{ fontSize: 11, color: '#71717A' }}>{card.recipientEmail || 'Direct purchase / in-store'}</div>
                      </div>
                    </td>
                    <td style={styles.td}>
                      <div style={{ color: card.currentBalanceMinor > 0 ? '#10B981' : '#71717A', fontWeight: 600 }}>
                        ₹{(card.currentBalanceMinor / 100).toLocaleString('en-IN')}
                      </div>
                      <div style={styles.progressBar}>
                        <div style={{ ...styles.progressFill, width: `${percentRemaining}%` }} />
                      </div>
                    </td>
                    <td style={styles.td}>
                      ₹{(card.initialBalanceMinor / 100).toLocaleString('en-IN')}
                    </td>
                    <td style={styles.td}>
                      <span style={styles.statusBadge(card.isActive, card.currentBalanceMinor)}>
                        {!card.isActive ? 'INACTIVE' : card.currentBalanceMinor === 0 ? 'REDEEMED' : 'ACTIVE'}
                      </span>
                    </td>
                    <td style={styles.td}>
                      <div style={{ fontSize: 12, color: '#A1A1AA' }}>{card.expiresAt || 'No Expiry'}</div>
                    </td>
                    <td style={styles.tdRight}>
                      <button
                        onClick={() => toggleActive(card.id)}
                        style={styles.toggleBtn(card.isActive)}
                        title={card.isActive ? 'Deactivate Card' : 'Activate Card'}
                      >
                        <Power size={13} />
                        {card.isActive ? 'Disable' : 'Enable'}
                      </button>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {showModal && (
        <div style={styles.modalOverlay} onClick={() => setShowModal(false)}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h3 style={styles.modalTitle}>Issue New Gift Card</h3>
            <p style={styles.modalSubtitle}>Create a store credit voucher with custom amount and recipient delivery</p>

            <form onSubmit={handleCreate} style={styles.form}>
              <div style={styles.field}>
                <label style={styles.label}>Gift Card Amount (INR ₹)</label>
                <div style={styles.presetRow}>
                  {['2000', '5000', '10000', '25000'].map((preset) => (
                    <button
                      type="button"
                      key={preset}
                      onClick={() => setAmount(preset)}
                      style={{
                        ...styles.presetBtn,
                        ...(amount === preset ? styles.presetBtnActive : {}),
                      }}
                    >
                      ₹{Number(preset).toLocaleString('en-IN')}
                    </button>
                  ))}
                </div>
                <input
                  type="number"
                  min="100"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  style={styles.input}
                  required
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Gift Card Code</label>
                <input
                  type="text"
                  value={customCode}
                  onChange={(e) => setCustomCode(e.target.value)}
                  style={styles.input}
                  placeholder="ZARIN-XXXX-XXXX-XXXX"
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Recipient Name (Optional)</label>
                <input
                  type="text"
                  value={recipientName}
                  onChange={(e) => setRecipientName(e.target.value)}
                  style={styles.input}
                  placeholder="e.g. Siddharth Rao"
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Recipient Email (Optional)</label>
                <input
                  type="email"
                  value={recipientEmail}
                  onChange={(e) => setRecipientEmail(e.target.value)}
                  style={styles.input}
                  placeholder="siddharth@example.com"
                />
              </div>

              <div style={styles.field}>
                <label style={styles.label}>Personal Message (Optional)</label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  style={{ ...styles.input, height: 70, resize: 'none' }}
                  placeholder="Warm wishes for your celebration..."
                />
              </div>

              <div style={styles.modalActions}>
                <button type="button" onClick={() => setShowModal(false)} style={styles.cancelBtn}>
                  Cancel
                </button>
                <button type="submit" style={styles.submitBtn}>
                  Issue Gift Card
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

const styles: Record<string, any> = {
  page: {
    padding: '28px 32px',
    maxWidth: 1200,
    margin: '0 auto',
    color: '#E5E5E5',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: 12,
    marginBottom: 24,
  },
  title: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 26,
    fontWeight: 600,
    color: '#FFFFFF',
    margin: 0,
  },
  subtitle: {
    fontSize: 13,
    color: '#8A8A8E',
    marginTop: 6,
  },
  btnPrimary: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    background: '#C9A84C',
    color: '#0A0A0A',
    border: 'none',
    padding: '9px 16px',
    borderRadius: 6,
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  },
  toolbar: {
    marginBottom: 20,
  },
  searchBox: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 6,
    padding: '8px 14px',
    maxWidth: 400,
  },
  searchInput: {
    background: 'transparent',
    border: 'none',
    color: '#FFF',
    fontSize: 13,
    outline: 'none',
    width: '100%',
  },
  tableCard: {
    background: '#141414',
    border: '1px solid #262626',
    borderRadius: 8,
    overflow: 'hidden',
    overflowX: 'auto',
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    textAlign: 'left',
  },
  th: {
    padding: '12px 18px',
    background: '#1A1A1A',
    color: '#8A8A8E',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #262626',
  },
  thRight: {
    padding: '12px 18px',
    background: '#1A1A1A',
    color: '#8A8A8E',
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    borderBottom: '1px solid #262626',
    textAlign: 'right',
  },
  tr: {
    borderBottom: '1px solid #1F1F1F',
  },
  td: {
    padding: '14px 18px',
    fontSize: 13,
    color: '#D4D4D8',
    verticalAlign: 'middle',
  },
  tdRight: {
    padding: '14px 18px',
    textAlign: 'right',
    verticalAlign: 'middle',
  },
  codeRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  codeText: {
    fontFamily: 'monospace',
    color: '#FFF',
    background: '#222',
    padding: '3px 8px',
    borderRadius: 4,
    fontSize: 12,
  },
  iconBtn: {
    background: 'transparent',
    border: 'none',
    cursor: 'pointer',
    padding: 3,
    display: 'flex',
    alignItems: 'center',
  },
  progressBar: {
    width: 90,
    height: 4,
    background: '#27272A',
    borderRadius: 2,
    marginTop: 4,
    overflow: 'hidden',
  },
  progressFill: {
    height: '100%',
    background: '#10B981',
    borderRadius: 2,
  },
  statusBadge: (active: boolean, balance: number) => ({
    fontSize: 10,
    fontWeight: 600,
    letterSpacing: '0.05em',
    padding: '2px 8px',
    borderRadius: 4,
    background: !active
      ? 'rgba(239, 68, 68, 0.15)'
      : balance === 0
      ? 'rgba(113, 113, 122, 0.15)'
      : 'rgba(16, 185, 129, 0.15)',
    color: !active ? '#EF4444' : balance === 0 ? '#A1A1AA' : '#10B981',
  }),
  toggleBtn: (active: boolean) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 5,
    background: active ? '#27272A' : 'rgba(16, 185, 129, 0.2)',
    color: active ? '#A1A1AA' : '#10B981',
    border: 'none',
    padding: '4px 10px',
    borderRadius: 4,
    fontSize: 11,
    cursor: 'pointer',
  }),
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'rgba(0, 0, 0, 0.75)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
  },
  modal: {
    background: '#141414',
    border: '1px solid #2A2A2A',
    borderRadius: 10,
    width: 480,
    maxWidth: '95vw',
    padding: 24,
  },
  modalTitle: {
    fontFamily: "'Playfair Display', serif",
    fontSize: 20,
    fontWeight: 600,
    color: '#FFF',
    margin: 0,
  },
  modalSubtitle: {
    fontSize: 12,
    color: '#8A8A8E',
    marginTop: 4,
    marginBottom: 20,
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 14,
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: 6,
  },
  label: {
    fontSize: 12,
    fontWeight: 500,
    color: '#A1A1AA',
  },
  presetRow: {
    display: 'flex',
    gap: 8,
    marginBottom: 6,
  },
  presetBtn: {
    flex: 1,
    background: '#1F1F1F',
    border: '1px solid #333',
    borderRadius: 5,
    padding: '6px 0',
    color: '#CCC',
    fontSize: 12,
    cursor: 'pointer',
  },
  presetBtnActive: {
    background: 'rgba(201, 168, 76, 0.15)',
    borderColor: '#C9A84C',
    color: '#C9A84C',
    fontWeight: 600,
  },
  input: {
    background: '#1F1F1F',
    border: '1px solid #333',
    borderRadius: 6,
    padding: '8px 12px',
    color: '#FFF',
    fontSize: 13,
    outline: 'none',
  },
  modalActions: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 10,
  },
  cancelBtn: {
    background: '#27272A',
    border: 'none',
    borderRadius: 6,
    padding: '8px 16px',
    color: '#CCC',
    fontSize: 13,
    cursor: 'pointer',
  },
  submitBtn: {
    background: '#C9A84C',
    border: 'none',
    borderRadius: 6,
    padding: '8px 18px',
    color: '#0A0A0A',
    fontWeight: 600,
    fontSize: 13,
    cursor: 'pointer',
  },
};
